// THE single resolver for "which backend is this console talking to".
//
// There used to be two: this generic derivation (in backOfficeApi) and a second,
// stale copy inside authClient.js gated on a hardcoded 8-entry BACKEND_DOMAINS
// allowlist. They agreed on tkt.ug/fanpass and disagreed everywhere else:
//
//   backoffice.moments.ug → backOfficeApi: backend.moments.ug | authClient: backend.tkt.ug
//   backoffice.mookh.com  → backOfficeApi: backend.mookh.com  | authClient: backend.tkt.ug
//
// The session cookie lives on the host backOfficeApi authenticated against, so every
// better-auth SDK call (admin.createUser, organization.inviteMember, the sign-in OTP
// itself) went to a DIFFERENT registrable domain with no cookie — better-auth's
// /admin/create-user answers a session-less request with 401 UNAUTHORIZED (a real
// admin who merely lacks the permission gets 403), surfacing in the console as a
// bare "Could not create user". One resolver, one answer, no drift.
//
// Derivation is generic — backoffice.<domain> → backend.<domain> — so a new product
// domain works without an allowlist edit. Local/preview hosts have no backend.<domain>
// sibling, so they fall back to the build-time override.
const isLocalOrPreviewHost = (hostname) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '0.0.0.0' ||
  /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || // bare IP
  hostname.endsWith('.local') ||
  hostname.endsWith('.workers.dev');          // CF preview mirror has no backend.<domain>

// "which <sub>.<domain> sibling of the host we're served on" — the ONE place that idiom
// lives. The console derives three of them: backend.<domain> (API), apis.<domain> (the
// ticket-PDF pass service) and ws.<domain> (the WhatsApp socket, which now proxies to the
// API's own port rather than a listener of its own). They were three hand-copied blocks
// sharing one private predicate, which is how deleting that predicate took out two call
// sites nobody had grepped for. `fallback` covers local/preview hosts, where no such
// sibling exists; `ws` swaps http(s) → ws(s).
export const derivedHostUrl = (sub, fallback, { ws = false } = {}) => {
  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0]; // strip any :port
    if (!isLocalOrPreviewHost(hostname)) {
      const parts = hostname.split('.');
      // "<sub>.<domain.tld>" → strip the first label; a bare apex domain is used as-is.
      const domain = parts.length > 2 ? parts.slice(1).join('.') : hostname;
      const scheme = ws ? (protocol === 'https:' ? 'wss:' : 'ws:') : protocol;
      return `${scheme}//${sub}.${domain}`;
    }
  }
  return typeof fallback === 'function' ? fallback() : fallback;
};

export const getBackendUrl = () =>
  derivedHostUrl('backend', () => import.meta.env.VITE_TKT_BACKEND_URL || 'http://127.0.0.1:3001');
